import "./style.css";
import { hyphenateSync as hyphenate } from "hyphen/de";

type viewType = "editor" | "preview";
let currentView: viewType = "editor";

const previewButton =
  document.querySelector<HTMLButtonElement>("#previewButton");
const editButton = document.querySelector<HTMLButtonElement>("#editButton");
const editorPanel = document.querySelector<HTMLDivElement>("#editorPanel");
const titleInput = document.querySelector<HTMLInputElement>("#titleInput");
const editorTextArea = document.querySelector<HTMLTextAreaElement>("#editor");
const previewPanel = document.querySelector<HTMLDivElement>("#previewPanel");
const previewDiv = document.querySelector<HTMLDivElement>("#preview");

["input", "change"].forEach((f) => {
  editorTextArea?.addEventListener(f, () => {
    save();
  });
  titleInput?.addEventListener(f, () => {
    save();
  });
});

const uppercaseCheckbox =
  document.querySelector<HTMLInputElement>("#uppercaseCheckbox");
const hyphenateCheckbox = document.querySelector<HTMLInputElement>(
  "#hyphenationCheckbox",
);
const hyphenateHyphenCheckbox = document.querySelector<HTMLInputElement>(
  "#hyphenationHyphenCheckbox",
);
const colorizeCheckbox =
  document.querySelector<HTMLInputElement>("#colorizeCheckbox");
[
  uppercaseCheckbox,
  hyphenateCheckbox,
  hyphenateHyphenCheckbox,
  colorizeCheckbox,
].forEach((e) => {
  if (!e) {
    throw new Error(`Element not found: ${e}`);
  }
  e.addEventListener("change", () => {
    save();
    if (currentView === "preview") {
      preview();
    }
  });
});

hyphenateCheckbox?.addEventListener("change", () => {
  if (!hyphenateCheckbox!.checked) {
    hyphenateHyphenCheckbox!.checked = false;
  }
});

document.addEventListener("DOMContentLoaded", () => {
  restore();
});

previewButton?.addEventListener("click", preview);
editButton?.addEventListener("click", edit);
const printButton = document.querySelector<HTMLButtonElement>("#printButton");
printButton?.addEventListener("click", print);
const resetButton = document.querySelector<HTMLButtonElement>("#resetButton");
resetButton?.addEventListener("click", reset);

function preview() {
  const pureText = editorTextArea?.value || "";
  const title = titleInput?.value || "";
  editorPanel!.style.display = "none";
  previewPanel!.style.display = "block";
  currentView = "preview";
  previewDiv!.innerHTML = render(
    title,
    pureText,
    uppercaseCheckbox!.checked,
    hyphenateCheckbox!.checked,
    hyphenateHyphenCheckbox!.checked,
    colorizeCheckbox!.checked,
  );
}

function edit() {
  previewPanel!.style.display = "none";
  editorPanel!.style.display = "block";
  currentView = "editor";
}

const colors = [
  "text-amber-600",
  "text-emerald-600",
  "text-rose-600",
  "text-indigo-600",
];

function render(
  title: string,
  input: string,
  toUpper: boolean,
  hyphenateWords: boolean,
  hyphenateWithHyphen: boolean,
  colorizeSyllables: boolean,
): string {
  const titleHtml = title
    ? `<h1 class="text-4xl font-semibold text-stone-900 mb-6 text-center">${processString(title, toUpper, hyphenateWords, hyphenateWithHyphen, colorizeSyllables, false)}</h1>`
    : "";
  const contentHtml = processString(
    input,
    toUpper,
    hyphenateWords,
    hyphenateWithHyphen,
    colorizeSyllables,
    true,
  );
  return `${titleHtml}<div class="prose-stone">${contentHtml}</div>`;
}

function processString(
  input: string,
  toUpper: boolean,
  hyphenateWords: boolean,
  hyphenateWithHyphen: boolean,
  colorizeSyllables: boolean,
  wrapParagraphs: boolean,
) {
  let output = input;
  if (toUpper) {
    output = output.toUpperCase();
  }
  if (hyphenateWords) {
    output = hyphenate(output, { minWordLength: 3, hyphenChar: "-" });
  }
  let colorIndex = 0;
  output = output
    .split(/\n\n/)
    .map((paragraph) => {
      paragraph = paragraph.replaceAll("\n", "<br>");
      paragraph = paragraph
        .split(/\s/)
        .map((word) => {
          if (colorizeSyllables) {
            return word
              .split(/[-=]/)
              .map((syl) => {
                const color = colors[colorIndex++ % colors.length];
                return `<span class="${color} font-medium">${syl}</span>`;
              })
              .join(hyphenateWithHyphen ? "-" : "");
          }
          return word;
        })
        .join(" ");
      if (wrapParagraphs) {
        return `<p class="text-lg leading-relaxed mb-4">${paragraph}</p>`;
      }
      return paragraph;
    })
    .join("");
  return output;
}

function print() {
  window.print();
}

function save() {
  localStorage.setItem("title", titleInput?.value || "");
  localStorage.setItem("story", editorTextArea?.value || "");
  localStorage.setItem(
    "uppercase",
    uppercaseCheckbox!.checked ? "true" : "false",
  );
  localStorage.setItem(
    "hyphenate",
    hyphenateCheckbox!.checked ? "true" : "false",
  );
  localStorage.setItem(
    "hyphenateHyphen",
    hyphenateHyphenCheckbox!.checked ? "true" : "false",
  );
  localStorage.setItem(
    "colorize",
    colorizeCheckbox!.checked ? "true" : "false",
  );
}

function restore() {
  const savedTitle = localStorage.getItem("title") ?? "";
  const savedStory = localStorage.getItem("story") ?? "";
  const savedUppercase = localStorage.getItem("uppercase") === "true";
  const savedHyphenate = localStorage.getItem("hyphenate") === "true";
  const savedHyphenateHyphen =
    localStorage.getItem("hyphenateHyphen") === "true";
  const savedColorize = localStorage.getItem("colorize") === "true";

  titleInput!.value = savedTitle;
  editorTextArea!.value = savedStory;
  uppercaseCheckbox!.checked = savedUppercase;
  hyphenateCheckbox!.checked = savedHyphenate;
  hyphenateHyphenCheckbox!.checked = savedHyphenateHyphen;
  colorizeCheckbox!.checked = savedColorize;
}

function reset() {
  if (confirm("Möchtest du wirklich alles zurücksetzen?")) {
    titleInput!.value = "";
    editorTextArea!.value = "";
    uppercaseCheckbox!.checked = true;
    hyphenateCheckbox!.checked = true;
    hyphenateHyphenCheckbox!.checked = true;
    colorizeCheckbox!.checked = true;
    save();
  }
}
